import React from "react";
import { useSelector } from "react-redux";
import { NMStatsTable, BarChart } from "@neumetric/blueprint-lib/components";
import { getDepartments } from "@neumetric/blueprint-lib/store";
import { getMitigatorDashboardStatsData } from "../../../store";
import { ExportSection } from "../ExportSection";
import ExportAxisLabel from "../ExportAxisLabel";

const SeverityColumns = [
  {
    typeRepresentation: "TITLE",
    title: "",
    attribute: "department",
    width: "17%",
  },
  {
    title: "OVERALL",
    id: "Overall",
    attribute: "total",
    width: "11%",
    highlight: { header: true, cell: true },
    typeRepresentation: "DOT",
    overallFlag: true,
    align: "center-right",
  },
  {
    title: "Emergency",
    id: "emergency",
    attribute: "emergency",
    width: "13%",
    typeRepresentation: "DOT",
    align: "center-right",
  },
  {
    title: "Urgent",
    id: "urgent",
    attribute: "urgent",
    typeRepresentation: "DOT",
    align: "center-right",
  },
  {
    title: "Critical",
    id: "critical",
    attribute: "critical",
    typeRepresentation: "DOT",
    align: "center-right",
  },
  {
    title: "High",
    id: "high",
    attribute: "high",
    typeRepresentation: "DOT",
    align: "center-right",
  },
  {
    title: "Medium",
    id: "medium",
    attribute: "medium",
    typeRepresentation: "DOT",
    align: "center-right",
  },
  {
    title: "Low",
    id: "low",
    attribute: "low",
    typeRepresentation: "DOT",
    align: "center-right",
  },
  {
    title: "Minimal",
    id: "minimal",
    attribute: "minimal",
    typeRepresentation: "DOT",
    align: "center-right",
  },
  {
    title: "None",
    id: "INVALID",
    attribute: "other",
    typeRepresentation: "DOT",
    align: "center-right",
  },
];

const SEVERITY_ORDER = [
  "None",
  "Minimal",
  "Low",
  "Medium",
  "High",
  "Critical",
  "Urgent",
  "Emergency",
];

const parseKey = (k) => {
  if (typeof k !== "string") return { department: "", value: null };
  const safe = k.replace(/\bnil\b/g, "null");
  try {
    const arr = JSON.parse(safe);
    return { department: arr?.[0], value: arr?.[1] };
  } catch {
    return { department: "", value: null };
  }
};

const emptyRow = (department) => ({
  department,
  total: 0,
  emergency: 0,
  urgent: 0,
  critical: 0,
  high: 0,
  medium: 0,
  low: 0,
  minimal: 0,
  other: 0,
});

const severityToAttr = (severity) => {
  const s = (severity || "").toString().trim().toLowerCase();
  if (
    [
      "emergency",
      "urgent",
      "critical",
      "high",
      "medium",
      "low",
      "minimal",
    ].includes(s)
  )
    return s;
  return "other";
};

export const DepartmentVsSeverity = ({ user }) => {
  const mitigatorDashboardData = useSelector(getMitigatorDashboardStatsData);
  const departments = useSelector(getDepartments);
  const stats = mitigatorDashboardData?.department_vs_severity || {};

  const companyLabel = user?.org?.brandname ?? "Luciance";

  const dashZero = (v) => {
    const n = Number(v) || 0;
    return n === 0 ? "-" : n;
  };

  const toTableRow = (r) => ({
    ...r,
    total: dashZero(r.total),
    emergency: dashZero(r.emergency),
    urgent: dashZero(r.urgent),
    critical: dashZero(r.critical),
    high: dashZero(r.high),
    medium: dashZero(r.medium),
    low: dashZero(r.low),
    minimal: dashZero(r.minimal),
    other: dashZero(r.other),
  });

  const { tableRows, datasets } = React.useMemo(() => {
    const rowsByDept = {};
    (departments || []).forEach((d) => (rowsByDept[d.id] = emptyRow(d.id)));

    Object.entries(stats || {}).forEach(([k, count]) => {
      const { department, value: sev } = parseKey(k);
      if (!department) return;

      if (!rowsByDept[department])
        rowsByDept[department] = emptyRow(department);

      const attr = severityToAttr(sev);
      const n = Number(count) || 0;

      rowsByDept[department][attr] += n;
      rowsByDept[department].total += n;
    });

    const deptList = departments?.length
      ? departments.map((d) => d.id)
      : Object.keys(rowsByDept);
    const numericRows = deptList.map((dep) => rowsByDept[dep] || emptyRow(dep));

    const totalRowNumeric = numericRows.reduce(
      (acc, r) => ({
        ...acc,
        total: acc.total + (Number(r.total) || 0),
        emergency: acc.emergency + (Number(r.emergency) || 0),
        urgent: acc.urgent + (Number(r.urgent) || 0),
        critical: acc.critical + (Number(r.critical) || 0),
        high: acc.high + (Number(r.high) || 0),
        medium: acc.medium + (Number(r.medium) || 0),
        low: acc.low + (Number(r.low) || 0),
        minimal: acc.minimal + (Number(r.minimal) || 0),
        other: acc.other + (Number(r.other) || 0),
      }),
      emptyRow(companyLabel),
    );

    const hasAnyData = (Number(totalRowNumeric.total) || 0) > 0;

    const computedTableRows = hasAnyData
      ? [
          {
            ...toTableRow(totalRowNumeric),
            department: companyLabel,
            highlight: true,
          },
          ...numericRows.map(toTableRow),
        ]
      : [];

    const computedDatasets = {};
    deptList.forEach((dep) => {
      const r = rowsByDept[dep] || emptyRow(dep);
      computedDatasets[dep] = SEVERITY_ORDER.map((label) => {
        const key = label === "None" ? "other" : label.toLowerCase();
        return { key, label, value: Number(r[key] || 0) };
      });
    });

    return { tableRows: computedTableRows, datasets: computedDatasets };
  }, [departments, stats, companyLabel]);

  const csvSeverityColumns = React.useMemo(() => {
    if (!Array.isArray(SeverityColumns) || SeverityColumns.length === 0)
      return [];

    return SeverityColumns.map((col, idx) => {
      if (idx !== 0) return col;

      return {
        ...col,
        title: "Risks (Department vs Severity)", // CSV header only
      };
    });
  }, [SeverityColumns]);

  return (
    <div className="d-flex gap-20 p20">
      <ExportSection
        index={1}
        moduleKey="Mitigator_Department_vs_Severity_Status"
        exportType="table"
        exportData={{
          title: "Risks - Department vs Severity",
          columns: csvSeverityColumns,
          rows: tableRows,
        }}
        style={{ flex: "1 1 55%", maxWidth: "60%" }}
      >
        <NMStatsTable
          title="Risks"
          subTitle="Department vs Severity"
          columns={SeverityColumns}
          dataSource={tableRows}
        />
      </ExportSection>

      <ExportSection
        index={2}
        moduleKey="Mitigator_Department_vs_Severity_Status"
        style={{
          flex: "1 1 45%",
          maxWidth: "50%",
          position: "relative",
        }}
      >
        <ExportAxisLabel text="Department" />

        <BarChart
          title="Risks"
          subtitle="Department vs Severity"
          label={{ xAxis: "Risks", yAxis: "" }}
          datasets={datasets}
          stackType="normal"
          classes={{ root: "h-575-px mt-12" }}
        />
      </ExportSection>
    </div>
  );
};
